import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { getSeoForPath, isPublicRoute, OG_IMAGE } from "../lib/seo";

/**
 * Updates the browser tab title & common meta tags on route changes.
 * The app name is always prefixed.
 */
const APP_NAME = "TrésoPilot";

const TITLES_BY_PATH = {
  "/": "Accueil",
  "/landing": "Accueil",
  "/login": "Connexion",
  "/register": "Créer un compte",
  "/logout": "Déconnexion",
  "/authcallback": "Connexion",
  "/resetpassword": "Réinitialiser le mot de passe",

  "/dashboard": "Tableau de bord",
  "/revenues": "Revenus",
  "/charges": "Charges",
  "/factures": "Factures",
  "/deadlines": "Échéances",
  "/vat-periods": "TVA",
  "/envelopes": "Enveloppes",
  "/goals": "Objectifs",
  "/reports": "Rapports",
  "/exports": "Exports",
  "/billing": "Abonnement",
  "/payment-success": "Paiement",
  "/settings": "Paramètres",
  "/adminreminders": "Admin – Rappels",
  "/onboarding": "Onboarding",
  "/simulations": "Simulations",
  "/previsionnel": "Prévisionnel",
  "/salaire": "Salaire",

  "/faq": "FAQ",
  "/contact": "Contact",
  "/mentionslegales": "Mentions légales",
  "/cgu": "CGU",
  "/confidentialite": "Politique de confidentialité",
  "/cookies": "Cookies",
  "/disclaimer": "Avertissement",
  "/unsubscribe": "Désinscription",
};

const DESCRIPTIONS_BY_PATH = {
  "/": "Suivi de trésorerie micro-entreprise, estimation du vrai net, échéances URSSAF/TVA/CFE et rappels.",
  "/login": "Connectez-vous à TrésoPilot.",
  "/register": "Créez votre compte TrésoPilot.",
  "/dashboard": "Votre tableau de bord de trésorerie et d’échéances.",
  "/revenues": "Gérez vos revenus et votre chiffre d’affaires.",
  "/charges": "Suivez vos charges et dépenses.",
  "/factures": "Gérez vos factures.",
  "/deadlines": "Anticipez vos échéances URSSAF, TVA, CFE.",
  "/vatperiods": "Suivi des périodes de TVA.",
  "/envelopes": "Créez des enveloppes pour provisionner vos dépenses.",
  "/goals": "Fixez vos objectifs et suivez vos contributions.",
  "/reports": "Rapports financiers et synthèses.",
  "/exports": "Exports et historiques.",
  "/billing": "Choisissez votre offre TrésoPilot.",
  "/settings": "Paramètres de votre compte TrésoPilot.",
  "/faq": "Questions fréquentes sur TrésoPilot.",
  "/contact": "Contactez l’équipe TrésoPilot.",
  "/mentionslegales": "Mentions légales TrésoPilot.",
  "/cgu": "Conditions générales d’utilisation TrésoPilot.",
  "/confidentialite": "Politique de confidentialité TrésoPilot.",
  "/cookies": "Politique de cookies TrésoPilot.",
  "/disclaimer": "Avertissement TrésoPilot."
};

function ensureMetaByName(name) {
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  return el;
}

function ensureMetaByProperty(property) {
  let el = document.querySelector(`meta[property="${property}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("property", property);
    document.head.appendChild(el);
  }
  return el;
}

function ensureCanonical() {
  let el = document.querySelector(`link[rel="canonical"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  return el;
}

function setMeta(name, content) {
  if (content == null) return;
  ensureMetaByName(name).setAttribute("content", String(content));
}

function setProperty(property, content) {
  if (content == null) return;
  ensureMetaByProperty(property).setAttribute("content", String(content));
}

function setCanonical(href) {
  if (href == null) return;
  ensureCanonical().setAttribute("href", String(href));
}

export default function TitleManager() {
  const location = useLocation();

  useEffect(() => {
    const rawPath = location.pathname || "/";
    const path = rawPath !== "/" ? rawPath.replace(/\/+$/, "") : "/";

    const publicRoute = isPublicRoute(path);
    const seo = getSeoForPath(path);

    const pageTitle = TITLES_BY_PATH[path];
    const appTitle = pageTitle ? `${APP_NAME} — ${pageTitle}` : APP_NAME;
    const fullTitle = publicRoute ? seo.title : appTitle;
    document.title = fullTitle;

    const desc = publicRoute
      ? seo.description
      : (DESCRIPTIONS_BY_PATH[path] || DESCRIPTIONS_BY_PATH["/"]);
    if (desc) {
      setMeta("description", desc);
      setProperty("og:description", desc);
      setMeta("twitter:description", desc);
    }

    // Canonical + robots
    setCanonical(publicRoute ? seo.canonical : `${window.location.origin}${path}`);
    setMeta("robots", publicRoute ? "index,follow" : "noindex,nofollow");

    setProperty("og:title", fullTitle);
    setMeta("twitter:title", fullTitle);

    setProperty("og:type", "website");
    setProperty("og:image", OG_IMAGE);
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:image", OG_IMAGE);

    // Keep og:url aligned with current route
    setProperty("og:url", publicRoute ? seo.canonical : `${window.location.origin}${path}`);
  }, [location.pathname]);

  return null;
}
